
import _ from 'lodash';

/**
 * InlineMessage
 *
 * Example usage:
 * ###### Register the Store ######
 * const { InlineMessageStore } = require('bundles/ui/components/InlineMessage');
 * app.registerStore(InlineMessageStore);
 *
 * ###### Fluxible Action ######
 *
 * const saveSessionPromise = require('...');
 * const {
 *   SET_SERVER_ERROR_MESSAGE,
 *   SET_ERROR_MESSAGE,
 *   SET_SUCCESS_MESSAGE
 * } = require('bundles/ui/InlineMessage');
 *
 * exports.saveSession = (actionContext, {messageKey, sessionId, session}) {
 *   saveSessionPromise({sessionId, session})
 *   	.then(() => {
 *   	  ...
 *   	  actionContext.dispatch(SET_SUCCESS_MESSAGE, {
 *   	    messageKey,
 *   	    contents: 'Success!'
 *   	  };
 *   	})
 *   	.catch((error) => {
 *      if (error.responseJSON) {
 *   	    actionContext.dispatch(SET_SERVER_ERROR_MESSAGE, {
 *   	      messageKey,
 *   	      contents: error.responseJSON
 *   	    });
 *   	  } else {
 *   	    actionContext.dispatch(SET_ERROR_MESSAGE, {
 *   	      messageKey,
 *   	      contents: 'Something went wrong!'
 *   	    });
 *   	  }
 *   	});
 * }
 *
 * ###### React Component ######
 *
 * const React = require('react');
 * const connectToStores = require('...');
 * const { saveSession } = require('[ACTION_ABOVE]');
 * const { ConnectedInlineMessage } = require('bundles/ui/components/InlineMessage');
 *
 * class SomeComponent extends React.Component {
 *   static propTypes = {
 *     sessionId: React.PropTypes.string.isRequired,
 *     session: React.PropTypes.object
 *   }
 *
 *   static contextTypes = {
 *     executeAction: React.PropTypes.func.isRequired
 *   }
 *
 *   constructor(props, context) {
 *     super(props, context);
 *
 *     this.messageKey = `${this.constructor.displayName}-${sessionId}`
 *   }
 *
 *   handleClick = () => {
 *     this.context.executeAction(saveSession, {
 *     	 messageKey: this.messageKey,
 *     	 sessionId: this.props.sessionId,
 *     	 session: this.props.session
 *     });
 *   }
 *
 *   render() {
 *     return (
 *       <div>
 *         <a onClick={this.handleClick} />
 *         <ConnectedInlineMessage messageKey={this.messageKey} />
 *       </div>
 *     );
 *   }
 * }
 *
 * module.exports = SomeComponent;
 */
import PropTypes from 'prop-types';
import BaseStore from 'vendor/cnpm/fluxible.v0-4/addons/BaseStore';
import connectToStores from 'vendor/cnpm/fluxible.v0-4/addons/connectToStores';

import waitFor from 'js/lib/waitFor';

import { InlineMessage, MessageTypes } from 'bundles/ui/components/InlineMessage';

/** **************************************************************************
 * Fluxible Action Names
 * ************************************************************************** */

const SET_SERVER_ERROR_MESSAGE = 'SET_SERVER_ERROR_MESSAGE';
const SET_ERROR_MESSAGE = 'SET_ERROR_MESSAGE';
const SET_SUCCESS_MESSAGE = 'SET_SUCCESS_MESSAGE';
const CLEAR_MESSAGE = 'CLEAR_MESSAGE';

/** **************************************************************************
 * Fluxible Store
 * ************************************************************************** */

class InlineMessageStore extends BaseStore {
  static storeName = 'InlineMessageStore';

  static handlers = {
    SET_SERVER_ERROR_MESSAGE({ messageKey, contents }: $TSFixMe) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'messages' does not exist on type '{ SET_... Remove this comment to see the full error message
      this.messages[messageKey] = {
        type: MessageTypes.SERVER_ERROR_TYPE,
        contents,
      };
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'emitChange' does not exist on type '{ SE... Remove this comment to see the full error message
      this.emitChange();
    },
    SET_ERROR_MESSAGE({ messageKey, contents }: $TSFixMe) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'messages' does not exist on type '{ SET_... Remove this comment to see the full error message
      this.messages[messageKey] = { type: MessageTypes.ERROR_TYPE, contents };
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'emitChange' does not exist on type '{ SE... Remove this comment to see the full error message
      this.emitChange();
    },
    SET_SUCCESS_MESSAGE({ messageKey, contents }: $TSFixMe) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'messages' does not exist on type '{ SET_... Remove this comment to see the full error message
      this.messages[messageKey] = { type: MessageTypes.SUCCESS_TYPE, contents };
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'emitChange' does not exist on type '{ SE... Remove this comment to see the full error message
      this.emitChange();
    },
    CLEAR_MESSAGE({ messageKey }: $TSFixMe) {
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'messages' does not exist on type '{ SET_... Remove this comment to see the full error message
      this.messages[messageKey] = null;
      // @ts-expect-error ts-migrate(2339) FIXME: Property 'emitChange' does not exist on type '{ SE... Remove this comment to see the full error message
      this.emitChange();
    },
  };

  messages = {};

  getMessageType(messageKey: $TSFixMe) {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    return this.messages[messageKey] ? this.messages[messageKey].type : null;
  }

  getMessage(messageKey: $TSFixMe) {
    // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
    return this.messages[messageKey] ? this.messages[messageKey].contents : null;
  }
}

const setServerErrorMessage = (actionContext: $TSFixMe, { messageKey, contents }: $TSFixMe) => {
  actionContext.dispatch(SET_SERVER_ERROR_MESSAGE, { messageKey, contents });
};

const setErrorMessage = (actionContext: $TSFixMe, { messageKey, contents }: $TSFixMe) => {
  actionContext.dispatch(SET_ERROR_MESSAGE, { messageKey, contents });
};

const setSuccessMessage = (actionContext: $TSFixMe, { messageKey, contents }: $TSFixMe) => {
  actionContext.dispatch(SET_SUCCESS_MESSAGE, { messageKey, contents });
};

const clearMessage = (actionContext: $TSFixMe, { messageKey }: $TSFixMe) => {
  actionContext.dispatch(CLEAR_MESSAGE, { messageKey });
};

/** **************************************************************************
 * Smart Component
 * ************************************************************************** */

const ConnectedInlineMessage = _.flowRight(
  connectToStores(['InlineMessageStore'], ({ InlineMessageStore }, props) => ({
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'messageKey' does not exist on type '{}'.
    type: InlineMessageStore.getMessageType(props.messageKey),
    // @ts-expect-error ts-migrate(2339) FIXME: Property 'messageKey' does not exist on type '{}'.
    contents: InlineMessageStore.getMessage(props.messageKey),
  })),
  // @ts-expect-error ts-migrate(2339) FIXME: Property 'type' does not exist on type '{}'.
  waitFor(({ type, contents }) => !!type && !!contents)
)(InlineMessage);

ConnectedInlineMessage.propTypes = {
  messageKey: PropTypes.string,
};

export {
  InlineMessageStore,
  ConnectedInlineMessage,
  setServerErrorMessage,
  setErrorMessage,
  setSuccessMessage,
  clearMessage,
  SET_SERVER_ERROR_MESSAGE,
  SET_ERROR_MESSAGE,
  SET_SUCCESS_MESSAGE,
  CLEAR_MESSAGE,
};
